import { useState } from "react";
import { Pencil, Trash2, Plus, Star, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, resolveImage, errorText } from "@/lib/api";
import { useAllBrands } from "@/components/Yachts";
import { PrimaryButton, GhostButton } from "./fields";
import YachtForm from "./YachtForm";

export default function YachtsManager() {
  const qc = useQueryClient();
  const { data: brands = [] } = useAllBrands();
  const [brandFilter, setBrandFilter] = useState("all");
  const [editing, setEditing] = useState(null); // null | "new" | yacht
  const { data: yachts = [] } = useQuery({
    queryKey: ["yachts", "all"],
    queryFn: async () => (await api.get("/admin/yachts")).data,
  });

  const refresh = () => qc.invalidateQueries({ queryKey: ["yachts"] }).then(() => qc.invalidateQueries({ queryKey: ["brand"] })).then(() => qc.invalidateQueries({ queryKey: ["brands"] }));
  const brandName = (slug) => brands.find((b) => b.slug === slug)?.name || slug;
  const shown = brandFilter === "all" ? yachts : yachts.filter((y) => y.brand_slug === brandFilter);

  const remove = async (y) => {
    if (!window.confirm(`Delete ${y.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/yachts/${y.id}`);
      toast.success("Model deleted.");
      refresh();
    } catch (err) {
      toast.error(errorText(err));
    }
  };

  return (
    <div data-testid="admin-yachts-manager">
      <div className="mb-8 flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap gap-2" data-testid="admin-yachts-brand-filter">
          {[{ slug: "all", name: "All" }, ...brands].map((b) => (
            <button
              key={b.slug}
              onClick={() => setBrandFilter(b.slug)}
              data-testid={`admin-yachts-filter-${b.slug}`}
              className={`rounded-full border px-4 py-1.5 text-[10px] uppercase tracking-[0.3em] transition-colors ${
                brandFilter === b.slug ? "border-gold bg-gold text-navy" : "border-navy-ink/15 text-mist hover:border-gold hover:text-gold"
              }`}
            >
              {b.name}
            </button>
          ))}
        </div>
        <PrimaryButton onClick={() => setEditing("new")} data-testid="admin-add-yacht-button" className="inline-flex items-center gap-2">
          <Plus size={12} /> Add model
        </PrimaryButton>
      </div>

      <p className="mb-4 text-[10px] uppercase tracking-[0.3em] text-mist" data-testid="admin-yachts-count">{shown.length} models</p>

      <div className="divide-y divide-navy-ink/10 border border-navy-ink/10 bg-white/40">
        {shown.map((y) => (
          <div key={y.id} className="flex flex-wrap items-center gap-5 p-4" data-testid={`admin-yacht-row-${y.id}`}>
            <img src={resolveImage(y.image)} alt="" className="h-16 w-28 shrink-0 object-cover" />
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold">{brandName(y.brand_slug)} {y.category && `· ${y.category}`}</p>
              <p className="mt-1 flex items-center gap-2 font-serif text-xl text-navy-ink" data-testid={`admin-yacht-row-${y.id}-name`}>
                {y.name}
                {y.featured && <Star size={12} className="fill-gold text-gold" />}
              </p>
              <p className="mt-1 truncate text-xs text-mist">{y.specs.map((s) => `${s.label}: ${s.value}`).join(" · ")}</p>
            </div>
            <div className="flex gap-2">
              <a href={`/yachts/${y.brand_slug}/${y.slug}`} target="_blank" rel="noreferrer" data-testid={`admin-yacht-row-${y.id}-view`} className="inline-flex items-center border border-navy-ink/20 px-3 py-2.5 text-[10px] uppercase tracking-[0.3em] text-mist hover:border-gold hover:text-gold"><ExternalLink size={12} /></a>
              <GhostButton onClick={() => setEditing(y)} data-testid={`admin-yacht-row-${y.id}-edit`} className="inline-flex items-center gap-2"><Pencil size={12} /> Edit</GhostButton>
              <GhostButton onClick={() => remove(y)} data-testid={`admin-yacht-row-${y.id}-delete`} className="inline-flex items-center gap-2 hover:border-red-700 hover:text-red-700"><Trash2 size={12} /></GhostButton>
            </div>
          </div>
        ))}
        {shown.length === 0 && <p className="p-8 text-sm text-mist" data-testid="admin-yachts-empty">No models yet.</p>}
      </div>

      {editing && (
        <YachtForm
          open
          onClose={() => setEditing(null)}
          yacht={editing === "new" ? null : editing}
          brands={brands}
          defaultBrand={brandFilter === "all" ? brands[0]?.slug : brandFilter}
          onSaved={refresh}
        />
      )}
    </div>
  );
}
